import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  runTransaction,
  writeBatch,
  Unsubscribe,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { createPrize, awardPrize, type PrizeType } from './prizes';
import { getRankingsByEventId, getRequestsByEventId, type SongRequest } from './requests';
import {
  buildUpcoming,
  revealablePicks,
  toReveal,
  type AwardDraft,
  type AwardPick,
  type AwardReveal,
  type AwardScores,
  type AwardVote,
  type EventAwards,
  type EventAwardsStatus,
} from '../utils/eventAwards';

export type { AwardDraft, AwardPick, AwardReveal, EventAwards } from '../utils/eventAwards';

export const AWARDS_ALREADY_STARTED = 'Awards already started';
export const AWARDS_NOTHING_TO_REVEAL = 'No award has a winner to reveal';
export const AWARDS_ALREADY_REVEALED = 'Already revealed on another screen';
export const AWARDS_PRIZE_FAILED = 'Award revealed, but the prize could not be sent';
export const AWARD_PRIZE_TIMEOUT_MINUTES = 5;

const PRIZE_TYPES: ReadonlySet<string> = new Set(['pick_next_song', 'auto_queue_bypass', 'custom', 'shoutout']);
const STATUSES: ReadonlySet<string> = new Set(['announced', 'revealing', 'done']);

export interface AwardInputs {
  requests: SongRequest[];
  votes: AwardVote[];
  rankingAttendeeIds: string[];
  scores: AwardScores | null;
}

export type AwardDraftValues = Pick<AwardDraft, 'picks' | 'showNamesEarly' | 'computedAt'>;

function draftRef(eventId: string) {
  return doc(db, 'eventAwardDrafts', eventId);
}

function awardsRef(eventId: string) {
  return doc(db, 'eventAwards', eventId);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function docToPick(raw: Record<string, unknown>): AwardPick {
  return {
    categoryId: String(raw.categoryId ?? ''),
    name: String(raw.name ?? ''),
    emoji: String(raw.emoji ?? ''),
    tagline: String(raw.tagline ?? ''),
    custom: raw.custom === true,
    winnerId: stringOrNull(raw.winnerId),
    winnerName: stringOrNull(raw.winnerName),
    stat: stringOrNull(raw.stat),
    overridden: raw.overridden === true,
    prizeType: typeof raw.prizeType === 'string' && PRIZE_TYPES.has(raw.prizeType) ? raw.prizeType as PrizeType : null,
    prizeCustomText: stringOrNull(raw.prizeCustomText),
  };
}

function docToReveal(raw: Record<string, unknown>): AwardReveal {
  return {
    categoryId: String(raw.categoryId ?? ''),
    name: String(raw.name ?? ''),
    emoji: String(raw.emoji ?? ''),
    tagline: String(raw.tagline ?? ''),
    winnerId: String(raw.winnerId ?? ''),
    winnerName: String(raw.winnerName ?? ''),
    stat: stringOrNull(raw.stat),
    howWon: stringOrNull(raw.howWon),
    revealedAt: String(raw.revealedAt ?? ''),
  };
}

export function docToAwardDraft(docSnap: DocumentSnapshot): AwardDraft {
  const data = docSnap.data() ?? {};
  return {
    eventId: data.eventId,
    djId: data.djId,
    picks: Array.isArray(data.picks) ? data.picks.map(docToPick) : [],
    showNamesEarly: data.showNamesEarly === true,
    computedAt: stringOrNull(data.computedAt),
    updatedAt: data.updatedAt,
  };
}

export function docToEventAwards(docSnap: DocumentSnapshot): EventAwards {
  const data = docSnap.data() ?? {};
  return {
    eventId: data.eventId,
    djId: data.djId,
    status: STATUSES.has(data.status) ? data.status as EventAwardsStatus : 'announced',
    upcoming: Array.isArray(data.upcoming)
      ? data.upcoming.map((item: Record<string, unknown>) => ({ name: String(item.name ?? ''), emoji: String(item.emoji ?? '') }))
      : [],
    totalCount: typeof data.totalCount === 'number' ? data.totalCount : 0,
    revealed: Array.isArray(data.revealed) ? data.revealed.map(docToReveal) : [],
    startedAt: stringOrNull(data.startedAt),
    updatedAt: data.updatedAt,
  };
}

export async function getAwardVotes(eventId: string): Promise<AwardVote[]> {
  const snapshot = await getDocs(query(collection(db, 'votes'), where('eventId', '==', eventId)));
  const votes: AwardVote[] = [];
  for (const voteDoc of snapshot.docs) {
    const data = voteDoc.data();
    const oderId = data.oderId || data.attendeeId;
    if (!data.requestId || !oderId) continue;
    votes.push({
      requestId: data.requestId,
      oderId,
      type: data.type === 'up' || data.type === 'down' ? data.type : undefined,
      votedAtMs: data.votedAt?.toMillis?.() ?? null,
    });
  }
  return votes;
}

export async function loadAwardInputs(eventId: string): Promise<AwardInputs> {
  const [requests, votes, rankings, scoresSnap] = await Promise.all([
    getRequestsByEventId(eventId),
    getAwardVotes(eventId),
    getRankingsByEventId(eventId),
    getDoc(doc(db, 'rankingScores', eventId)),
  ]);
  const scoresData = scoresSnap.exists() ? scoresSnap.data() : null;
  return {
    requests,
    votes,
    rankingAttendeeIds: rankings.map((ranking) => ranking.attendeeId).filter(Boolean),
    scores: scoresData
      ? { consensus: scoresData.consensus ?? {}, discovery: scoresData.discovery ?? {} }
      : null,
  };
}

export function subscribeToAwardDraft(eventId: string, callback: (draft: AwardDraft | null) => void): Unsubscribe {
  return onSnapshot(draftRef(eventId), (snapshot) => {
    callback(snapshot.exists() ? docToAwardDraft(snapshot) : null);
  }, (error) => {
    console.error('[EventAwards] Draft listener failed:', error);
    callback(null);
  });
}

export function subscribeToEventAwards(eventId: string, callback: (awards: EventAwards | null) => void): Unsubscribe {
  return onSnapshot(awardsRef(eventId), (snapshot) => {
    callback(snapshot.exists() ? docToEventAwards(snapshot) : null);
  }, (error) => {
    console.error('[EventAwards] Awards listener failed:', error);
    callback(null);
  });
}

export async function getEventAwards(eventId: string): Promise<EventAwards | null> {
  const snapshot = await getDoc(awardsRef(eventId));
  return snapshot.exists() ? docToEventAwards(snapshot) : null;
}

export async function saveAwardDraft(eventId: string, djId: string, values: AwardDraftValues): Promise<void> {
  const now = new Date().toISOString();
  const publicSnap = await getDoc(awardsRef(eventId));
  const batch = writeBatch(db);
  batch.set(draftRef(eventId), {
    eventId,
    djId,
    picks: values.picks,
    showNamesEarly: values.showNamesEarly,
    computedAt: values.computedAt,
    updatedAt: now,
  });
  if (publicSnap.exists() && publicSnap.data().status === 'announced') {
    batch.update(awardsRef(eventId), { upcoming: buildUpcoming(values.picks), updatedAt: now });
  }
  await batch.commit();
}

export async function announceAwardNames(eventId: string, djId: string, picks: AwardPick[]): Promise<void> {
  const ref = awardsRef(eventId);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (snapshot.exists() && snapshot.data().status !== 'announced') {
      throw new Error(AWARDS_ALREADY_STARTED);
    }
    transaction.set(ref, {
      eventId,
      djId,
      status: 'announced',
      upcoming: buildUpcoming(picks),
      totalCount: 0,
      revealed: [],
      startedAt: null,
      updatedAt: new Date().toISOString(),
    });
  });
}

export async function startAwardReveal(eventId: string, djId: string): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const [draftSnap, awardsSnap] = await Promise.all([
      transaction.get(draftRef(eventId)),
      transaction.get(awardsRef(eventId)),
    ]);
    const status = awardsSnap.exists() ? awardsSnap.data().status : null;
    if (status === 'revealing' || status === 'done') {
      throw new Error(AWARDS_ALREADY_STARTED);
    }
    const draft = draftSnap.exists() ? docToAwardDraft(draftSnap) : null;
    const picks = draft ? revealablePicks(draft.picks) : [];
    if (!draft || picks.length === 0) {
      throw new Error(AWARDS_NOTHING_TO_REVEAL);
    }
    const now = new Date().toISOString();
    transaction.set(awardsRef(eventId), {
      eventId,
      djId,
      status: 'revealing',
      upcoming: draft.showNamesEarly ? buildUpcoming(draft.picks) : [],
      totalCount: picks.length,
      revealed: [],
      startedAt: now,
      updatedAt: now,
    });
  });
}

export async function revealNextAward(eventId: string, djId: string, expectedIndex: number): Promise<AwardPick> {
  const pick = await runTransaction(db, async (transaction) => {
    const [draftSnap, awardsSnap] = await Promise.all([
      transaction.get(draftRef(eventId)),
      transaction.get(awardsRef(eventId)),
    ]);
    if (!draftSnap.exists() || !awardsSnap.exists()) {
      throw new Error(AWARDS_ALREADY_REVEALED);
    }
    const awards = docToEventAwards(awardsSnap);
    if (awards.status !== 'revealing' || awards.revealed.length !== expectedIndex) {
      throw new Error(AWARDS_ALREADY_REVEALED);
    }
    const next = revealablePicks(docToAwardDraft(draftSnap).picks)[expectedIndex];
    if (!next) {
      throw new Error(AWARDS_NOTHING_TO_REVEAL);
    }
    const now = new Date().toISOString();
    const revealed = [...awards.revealed, toReveal(next, now)];
    transaction.update(awardsRef(eventId), {
      revealed,
      status: revealed.length >= awards.totalCount ? 'done' : 'revealing',
      updatedAt: now,
    });
    return next;
  });

  if (pick.prizeType && pick.winnerId) {
    try {
      const prizeId = await createPrize(eventId, djId, pick.prizeType, {
        customText: pick.prizeCustomText ?? undefined,
        timeoutMinutes: AWARD_PRIZE_TIMEOUT_MINUTES,
        gameSource: 'awards',
        gameRoundId: `award-${pick.categoryId}`,
      });
      await awardPrize(prizeId, pick.winnerId, pick.winnerName ?? undefined);
    } catch (error) {
      console.error('[EventAwards] Prize for revealed award failed:', error);
      throw new Error(AWARDS_PRIZE_FAILED);
    }
  }
  return pick;
}

export async function resetEventAwards(eventId: string): Promise<void> {
  const batch = writeBatch(db);
  batch.delete(draftRef(eventId));
  batch.delete(awardsRef(eventId));
  await batch.commit();
}
